/**
 * AdminPermissionsDrawer — edição de permissões granulares por área.
 *
 * Pacote 6 — Sub-perfis Administrativos (Gestão de Admins).
 * Spec: docs/V2/07_REGRAS_NEGOCIO.md §15
 *
 * UX:
 *  - 4 áreas (general/tickets/financial/administrative)
 *  - 4 níveis por área (view/edit/approve/delete) — ModernSwitch
 *  - Admin Master = full nos 4 níveis da área "general". Limite hard: 3 contas.
 *  - Trata 403 / 422 BUSINESS_RULE com alerta vermelho persistente no topo.
 */

import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import Drawer from '../../components/products/details/Drawer';
import ModernSwitch from '../../components/forms/ModernSwitch';
import adminPermissionsV2API from '../../services/api/v2/adminPermissions';

const AREA_META = {
  general: {
    label: 'Geral / Técnico',
    description: 'Acesso geral à plataforma e operações técnicas. Full = Admin Master.',
    icon: 'mdi:shield-crown',
    color: 'purple',
  },
  tickets: {
    label: 'Suporte',
    description: 'Atendimento ao cliente e tickets de helpdesk.',
    icon: 'mdi:headphones',
    color: 'blue',
  },
  financial: {
    label: 'Financeiro',
    description: 'Saques, antecipações, taxas e movimentações financeiras.',
    icon: 'mdi:cash-multiple',
    color: 'green',
  },
  administrative: {
    label: 'Administrativo',
    description: 'Aprovação de produtos, contas bancárias e cadastros.',
    icon: 'mdi:clipboard-check',
    color: 'orange',
  },
};

const AREA_ORDER = ['general', 'tickets', 'financial', 'administrative'];
const LEVELS = [
  { key: 'can_view', label: 'Visualizar' },
  { key: 'can_edit', label: 'Editar' },
  { key: 'can_approve', label: 'Aprovar' },
  { key: 'can_delete', label: 'Deletar' },
];

const emptyAreaState = () => ({
  can_view: false,
  can_edit: false,
  can_approve: false,
  can_delete: false,
});

const initialPermissionsState = () => {
  const state = {};
  AREA_ORDER.forEach((area) => {
    state[area] = emptyAreaState();
  });
  return state;
};

const AdminPermissionsDrawer = ({ isOpen, onClose, admin, onSaved }) => {
  const [permissions, setPermissions] = useState(initialPermissionsState);
  const [submitting, setSubmitting] = useState(false);
  const [topError, setTopError] = useState('');

  // Hidrata estado quando abre / troca de admin
  useEffect(() => {
    if (!isOpen) return;
    setTopError('');
    setSubmitting(false);

    const fresh = initialPermissionsState();
    if (admin?.permissions && Array.isArray(admin.permissions)) {
      admin.permissions.forEach((p) => {
        if (fresh[p.area]) {
          fresh[p.area] = {
            can_view: !!p.can_view,
            can_edit: !!p.can_edit,
            can_approve: !!p.can_approve,
            can_delete: !!p.can_delete,
          };
        }
      });
    }
    setPermissions(fresh);
  }, [isOpen, admin]);

  const toggleLevel = (area, levelKey, nextValue) => {
    setPermissions((prev) => {
      const areaState = { ...(prev[area] || emptyAreaState()) };
      areaState[levelKey] = nextValue;

      // Regra UX: ao desligar Visualizar, desliga os demais (não faz sentido editar sem ver).
      if (levelKey === 'can_view' && !nextValue) {
        areaState.can_edit = false;
        areaState.can_approve = false;
        areaState.can_delete = false;
      }
      // Regra UX: ao ligar qualquer nível superior, liga Visualizar automaticamente.
      if (levelKey !== 'can_view' && nextValue) {
        areaState.can_view = true;
      }

      return { ...prev, [area]: areaState };
    });
  };

  const buildPayload = () => {
    const body = AREA_ORDER.map((area) => ({
      area,
      can_view: !!permissions[area]?.can_view,
      can_edit: !!permissions[area]?.can_edit,
      can_approve: !!permissions[area]?.can_approve,
      can_delete: !!permissions[area]?.can_delete,
    }));
    return { permissions: body };
  };

  const handleSubmit = async () => {
    if (!admin?.user_id || submitting) return;
    try {
      setSubmitting(true);
      setTopError('');
      const res = await adminPermissionsV2API.setPermissions(
        admin.user_id,
        buildPayload(),
      );
      toast.success('Permissões atualizadas com sucesso.');
      onSaved?.(res?.data?.permissions || []);
      onClose?.();
    } catch (err) {
      const code = err?.code || err?.response?.data?.code;
      const message = err?.message
        || err?.response?.data?.error
        || 'Erro ao salvar permissões.';

      const isAdminMasterLimit = code === 'BUSINESS_RULE'
        && typeof message === 'string'
        && message.toLowerCase().includes('admin master');

      if (isAdminMasterLimit || code === 'FORBIDDEN' || code === 'BUSINESS_RULE') {
        // Mantém alerta vermelho persistente no topo do drawer.
        setTopError(message);
      } else {
        toast.error(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={admin ? `Permissões: ${admin.name || admin.email}` : 'Permissões'}
      subtitle={admin?.email || ''}
      icon="mdi:shield-account"
      size="lg"
      onSubmit={handleSubmit}
      submitting={submitting}
      submitLabel="Salvar permissões"
    >
      {topError && (
        <div
          className="alert alert-danger d-flex align-items-start gap-2"
          role="alert"
          style={{ marginBottom: 16 }}
        >
          <Icon icon="mdi:alert-circle" width="20" />
          <div>
            <span style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
              Não foi possível salvar
            </span>
            <span>{topError}</span>
          </div>
        </div>
      )}

      <div
        className="alert alert-info d-flex align-items-start gap-2"
        role="status"
        style={{ marginBottom: 20 }}
      >
        <Icon icon="mdi:information" width="20" />
        <span>
          Marque os níveis que este administrador pode exercer em cada área.
          Ligar um nível superior liga &quot;Visualizar&quot; automaticamente.
        </span>
      </div>

      <div className="d-flex flex-column gap-3">
        {AREA_ORDER.map((area) => {
          const meta = AREA_META[area];
          const state = permissions[area] || emptyAreaState();
          const isAdminMaster = area === 'general'
            && state.can_view && state.can_edit && state.can_approve && state.can_delete;

          return (
            <div
              key={area}
              className="card"
              style={{
                borderRadius: 10,
                border: isAdminMaster ? '2px solid #8b5cf6' : '1px solid #e5e7eb',
                boxShadow: 'none',
              }}
            >
              <div className="card-body" style={{ padding: 16 }}>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <Icon icon={meta.icon} width="20" />
                  <span style={{ fontWeight: 600, fontSize: 15 }}>
                    {meta.label}
                  </span>
                  {isAdminMaster && (
                    <span
                      className="badge"
                      style={{
                        background: '#ede9fe',
                        color: '#6d28d9',
                        fontWeight: 600,
                      }}
                    >
                      Admin Master
                    </span>
                  )}
                </div>
                <span
                  style={{
                    display: 'block',
                    color: '#6b7280',
                    fontSize: 13,
                    marginBottom: 12,
                  }}
                >
                  {meta.description}
                </span>

                <div
                  className="d-flex flex-wrap gap-3"
                  style={{ rowGap: 12 }}
                >
                  {LEVELS.map((lvl) => (
                    <div key={lvl.key} style={{ minWidth: 130 }}>
                      <ModernSwitch
                        label={lvl.label}
                        size="sm"
                        color={meta.color}
                        checked={!!state[lvl.key]}
                        onChange={(next) => toggleLevel(area, lvl.key, next)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div
        className="text-muted"
        style={{ fontSize: 12, marginTop: 16, lineHeight: 1.5 }}
      >
        <Icon icon="mdi:shield-crown" width="14" /> Admin Master = todos os
        switches ligados na área <strong>Geral</strong>. Limite: 3 contas.
      </div>
    </Drawer>
  );
};

export default AdminPermissionsDrawer;
